import React from 'react';

interface SocialButtonsProps {
  githubUrl?: string;
  linkedinUrl?: string;
  cvUrl?: string; // URL vers le CV (PDF ou page)
  size?: number;
  variant?: 'minimal' | 'glass' | 'outline';
}

const GitHubIcon: React.FC<{ style?: React.CSSProperties }> = ({ style }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" style={style} aria-hidden="true">
    <path d="M12 .5C5.65.5.5 5.65.5 12c0 5.08 3.29 9.38 7.86 10.9.58.12.79-.25.79-.56 0-.27-.01-1.17-.02-2.12-3.2.7-3.88-1.36-3.88-1.36-.53-1.35-1.3-1.71-1.3-1.71-1.06-.72.08-.71.08-.71 1.17.08 1.78 1.2 1.78 1.2 1.04 1.78 2.72 1.27 3.38.97.11-.76.4-1.27.73-1.56-2.55-.29-5.23-1.28-5.23-5.7 0-1.26.45-2.29 1.19-3.09-.12-.29-.52-1.47.11-3.06 0 0 .97-.31 3.18 1.18a11.14 11.14 0 0 1 5.79 0c2.2-1.49 3.17-1.18 3.17-1.18.63 1.59.24 2.77.12 3.06.74.8 1.19 1.83 1.19 3.09 0 4.43-2.69 5.41-5.25 5.69.41.35.77 1.04.77 2.1 0 1.52-.01 2.74-.01 3.12 0 .31.21.68.8.56A11.52 11.52 0 0 0 23.5 12c0-6.35-5.15-11.5-11.5-11.5Z" />
  </svg>
);

const LinkedInIcon: React.FC<{ style?: React.CSSProperties }> = ({ style }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" style={style} aria-hidden="true">
    <path d="M4.98 3.5a2.5 2.5 0 1 0 0 5.001 2.5 2.5 0 0 0 0-5Zm.02 4.75H2V21h3V8.25Zm6.47 0H8.5V21h3v-6.38c0-1.69.32-3.32 2.41-3.32 2.05 0 2.07 1.92 2.07 3.43V21h3v-6.88c0-3.37-.72-5.97-4.64-5.97-1.88 0-3.14.76-3.68 1.65h-.04l-.14-.8Z" />
  </svg>
);

const CvIcon: React.FC<{ style?: React.CSSProperties }> = ({ style }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" style={style} aria-hidden="true">
    <path d="M7 2h7l5 5v13a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V4c0-1.1.9-2 2-2Z" />
    <path d="M14 2v5h5" />
    <path d="M9 12c0 1.6.7 3 2.5 3 1.4 0 2.3-.8 2.4-2" />
    <path d="M9 12c0-1.4.7-2.4 2.5-2.4 1.2 0 2 .5 2.3 1.6" />
  </svg>
);

const SocialButtons: React.FC<SocialButtonsProps> = ({
  githubUrl = 'https://github.com/USERNAME',
  linkedinUrl = 'https://www.linkedin.com/in/USERNAME',
  cvUrl = '/cv.pdf',
  size = 54,
  variant = 'minimal',
}) => {
  const baseCommon: React.CSSProperties = {
    width: size,
    height: size,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '0.6rem',
    cursor: 'pointer',
    transition: 'background 160ms ease, transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease',
    position: 'relative',
    outline: 'none',
    textDecoration: 'none',
  };

  const stylesByVariant: Record<string, React.CSSProperties> = {
    minimal: {
      // Fully transparent: no fill, no backdrop blur so stars remain crisp behind
      background: 'transparent',
      border: '1px solid rgba(255,255,255,0.42)',
      boxShadow: '0 4px 14px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,255,255,0.12)',
      // Removed backdropFilter to avoid smudging/softening starfield
    },
    glass: {
      background: 'linear-gradient(140deg, rgba(45,55,72,0.65), rgba(15,23,42,0.82))',
      border: '1px solid rgba(255,255,255,0.18)',
      boxShadow: '0 0 0 1px rgba(255,255,255,0.08), 0 6px 22px rgba(0,0,0,0.65), 0 0 18px -2px rgba(96,165,250,0.35)',
      backdropFilter: 'blur(8px)',
    },
    outline: {
      background: 'rgba(0,0,0,0.55)',
      border: '1px solid rgba(148,163,184,0.35)',
      boxShadow: '0 2px 10px rgba(0,0,0,0.6)',
    },
  };

  const commonStyle: React.CSSProperties = { ...baseCommon, ...stylesByVariant[variant] };

  const iconStyle: React.CSSProperties = {
    width: '58%',
    height: '58%',
    display: 'block',
    color: '#ffffff',
    filter: 'drop-shadow(0 1px 4px rgba(0,0,0,0.6)) drop-shadow(0 0 4px rgba(255,255,255,0.25))',
    transition: 'color 160ms ease, filter 160ms ease',
  };

  return (
    <div className="social-buttons-container">
      {/* Bouton CV au-dessus */}
      <a
        href={cvUrl}
        target="_blank"
        rel="noopener noreferrer"
        style={commonStyle}
        title="CV"
        onMouseEnter={(e) => {
          const el = e.currentTarget as HTMLAnchorElement;
          el.style.transform = 'translateY(-4px)';
          el.style.boxShadow = '0 8px 26px rgba(0,0,0,0.75), 0 0 0 1px rgba(255,255,255,0.18), 0 0 22px -2px rgba(255,255,255,0.40)';
          el.style.borderColor = 'rgba(255,255,255,0.55)';
        }}
        onMouseLeave={(e) => {
          const el = e.currentTarget as HTMLAnchorElement;
          el.style.transform = 'translateY(0)';
          el.style.boxShadow = stylesByVariant[variant].boxShadow || '0 4px 14px rgba(0,0,0,0.55)';
          el.style.borderColor = (stylesByVariant[variant].border as string) || 'rgba(255,255,255,0.42)';
        }}
        onFocus={(e) => {
          (e.currentTarget as HTMLAnchorElement).style.boxShadow = '0 0 0 2px rgba(255,255,255,0.45), 0 0 0 6px rgba(255,255,255,0.35)';
        }}
        onBlur={(e) => {
          (e.currentTarget as HTMLAnchorElement).style.boxShadow = stylesByVariant[variant].boxShadow || '0 4px 14px rgba(0,0,0,0.55)';
        }}
      >
        <CvIcon style={{ ...iconStyle, color: '#ffffff' }} />
        <span style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>CV</span>
      </a>
      <div style={{ display: 'flex', gap: '0.7rem' }}>
        <a
          href={linkedinUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={commonStyle}
          title="LinkedIn"
          onMouseEnter={(e) => {
            const el = e.currentTarget as HTMLAnchorElement;
            el.style.transform = 'translateY(-4px)';
            el.style.boxShadow = '0 8px 26px rgba(0,0,0,0.75), 0 0 0 1px rgba(255,255,255,0.15), 0 0 22px -2px rgba(14,118,223,0.45)';
            el.style.borderColor = 'rgba(255,255,255,0.5)';
          }}
          onMouseLeave={(e) => {
            const el = e.currentTarget as HTMLAnchorElement;
            el.style.transform = 'translateY(0)';
            el.style.boxShadow = stylesByVariant[variant].boxShadow || '0 4px 14px rgba(0,0,0,0.55)';
            el.style.borderColor = (stylesByVariant[variant].border as string) || 'rgba(255,255,255,0.28)';
          }}
          onFocus={(e) => {
            (e.currentTarget as HTMLAnchorElement).style.boxShadow = '0 0 0 2px rgba(255,255,255,0.35), 0 0 0 6px rgba(14,118,223,0.45)';
          }}
          onBlur={(e) => {
            (e.currentTarget as HTMLAnchorElement).style.boxShadow = stylesByVariant[variant].boxShadow || '0 4px 14px rgba(0,0,0,0.55)';
          }}
        >
          <LinkedInIcon style={{ ...iconStyle, color: '#0A66C2' }} />
          <span style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>LinkedIn</span>
        </a>
        <a
          href={githubUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={commonStyle}
          title="GitHub"
          onMouseEnter={(e) => {
            const el = e.currentTarget as HTMLAnchorElement;
            el.style.transform = 'translateY(-4px)';
            el.style.boxShadow = '0 8px 26px rgba(0,0,0,0.75), 0 0 0 1px rgba(255,255,255,0.15), 0 0 22px -2px rgba(255,255,255,0.45)';
            el.style.borderColor = 'rgba(255,255,255,0.5)';
          }}
          onMouseLeave={(e) => {
            const el = e.currentTarget as HTMLAnchorElement;
            el.style.transform = 'translateY(0)';
            el.style.boxShadow = stylesByVariant[variant].boxShadow || '0 4px 14px rgba(0,0,0,0.55)';
            el.style.borderColor = (stylesByVariant[variant].border as string) || 'rgba(255,255,255,0.28)';
          }}
          onFocus={(e) => {
            (e.currentTarget as HTMLAnchorElement).style.boxShadow = '0 0 0 2px rgba(255,255,255,0.45), 0 0 0 6px rgba(255,255,255,0.35)';
          }}
          onBlur={(e) => {
            (e.currentTarget as HTMLAnchorElement).style.boxShadow = stylesByVariant[variant].boxShadow || '0 4px 14px rgba(0,0,0,0.55)';
          }}
        >
          <GitHubIcon style={{ ...iconStyle, color: '#ffffff' }} />
          <span style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>GitHub</span>
        </a>
      </div>
    </div>
  );
};

export default SocialButtons;